namespace Solovis.Reports.ReportHelpers.HorizonPerformance {
  export class HorizonPerformanceReportGenerator {

    public static generateReportDefinition() {
      let groupings = new Array();

      const horizonPerformanceDataSet = {
        name: "performance",
        path: "/Solovis/Performance/HorizonPerformance",
        parameterMap: function (parameters, more) {
          if (parameters.groupingCodes) {
            parameters.groupings = [];
            parameters.groupingCodes.forEach(gc => {
              const matchGroups = groupings.filter(g => g.value === gc);
              matchGroups.forEach(mg => parameters.groupings.push(mg));
            })
          }
          return parameters;
        },
        postProcess: function (loadedDataSet, more) {
          loadedDataSet.lineItems.forEach(lineItem => {
            lineItem.statsByCode = {};
            lineItem.stats.forEach(stat => {
              if (stat.value && stat.value.propertyTypeId) {
                InvData.Util.TouchUpValueStringByPropertyType(stat.value.propertyTypeId, stat.value);
              }

              lineItem.statsByCode[stat.code] = stat;
            });
            if (lineItem.benchmarks) {
              lineItem.benchmarks.forEach(b => {
                b.statsByCode = {};
                b.stats.forEach(stat => b.statsByCode[stat.code] = stat);
              })
            }
          });

          const dataSet = {
            dataSet: loadedDataSet,
          };

          return dataSet;
        }
      };

      const loadStatOptions = async function (init) {
        await Rpc.invoke("~/Rpc/DataSource/GetHorizonPerformanceStatOptions", {},
          function (response, success) {
            if (success) {
              response.result.forEach(v => v.name = v.label);
            }
            init(response, success);
          })
      }

      const loadGroupingsOptions = async function (init) {
        await Rpc.invoke("~/Selectors/CompositeSelector/GetCompositeGroupings", {},
          function (response, success) {
            if (success) {
              response.result.forEach(v => v.value = v.label + v.declarationType);
              groupings = response.result;
            }
            init(response, success);
          })
      }

      const report = {
        parameters: [
          {
            name: "assetSelectionSet", label: "Asset Selection Set", parameterType: "assetSelectionSet",
            validation: { required: true }
          },
          {
            name: "groupingCodes", list: true, label: "Groupings", parameterType: "choice",
            settings: { optionsAsync: loadGroupingsOptions, isSearchable: true }
          },
          {
            name: "toDayId", label: "To Date", parameterType: "dateIndex",
            initialValue: InvData.Util.DateIndexHelper.getDateIndexOfLastDayInPreviousMonth(InvData.Util.DateIndexHelper.getDateIndexForToday())
          },
          {
            name: "statistics", label: "Statistics", parameterType: "columnSelector",
            settings: { optionsAsync: loadStatOptions }
          },
          {
            name: "advancedOptions", label: "Advanced Options", parameterType: "horizonPerformanceAdvancedOptions",
            initialValue: new Solovis.Reports.Parameters.HorizonPerformance.Options()
          },
        ],
        root: {
          elementName: "Root",
          layout: "Vertical",
          children: [
            {
              partControl: "Solovis.Reports.Parts.Tables.DataTableControl",
              elementName: "HorizonPerformanceTable",
              settings: {
                columns: function () {
                  const self: Solovis.Reports.Parts.Tables.DataTableControl = this;
                  const params = self.parameters;
                  const columns: any[] = [
                    {
                      id: "label", field: "label", label: "Label", dataType: "string", width: 275,
                      htmlFormatter: Solovis.Reports.Formatter.createAssetLabelFormatter({ resourceIdProperty: "resourceId" })
                    },
                  ];
                  const propFormatter = function (cellValue, cellContext) {
                    if (!cellValue) { return; }

                    const row = cellContext.row._backingRow || cellContext.row;

                    if (!row.statsByCode && row._groupKey) {
                      return row._groupKey;
                    }

                    const value = row.statsByCode[cellContext.col.id].value;

                    const container = Solovis.Reports.Formatter.formatResourcePropertyValueAsHtmlRaw(
                      value.propertyTypeId,
                      value.value,
                      value.valueStrings,
                      value.tagRefTagSetId,
                      cellContext.dataTable && cellContext.dataTable.printMode ? cellContext.dataTable.printMode : null);
                    return container;
                  };

                  const twrPerfUsed = Solovis.isNotNull(params.advancedOptions.twrPerformanceVariationCode);
                  const irrPerfUsed = Solovis.isNotNull(params.advancedOptions.irrPerformanceVariationCode);

                  params.statistics.forEach(stat => {
                    const isProp = stat.category === "Property";
                    const isPerf = stat.category === "Performance";
                    const isTwr = isPerf && stat.code.endsWith('TWR');
                    const isIrr = isPerf && stat.code.endsWith('IRR');

                    columns.push({
                      id: stat.code,
                      field: function (row) {
                        let rowToUse = row;
                        if (!row.statsByCode && row._backingRow) {
                          rowToUse = rowToUse._backingRow;
                        }

                        const val = rowToUse.statsByCode[stat.code];
                        if (val) {
                          if (isProp) {
                            return val.value.valueStrings.join(",");
                          }

                          return val.value;
                        }
                      },
                      label: stat.label,
                      dataType: stat.type,
                      htmlFormatter: isProp ? propFormatter : null,
                      superHeaders: (isTwr && twrPerfUsed) ? [params.advancedOptions.twrPerformanceVariationLabel || params.advancedOptions.twrPerformanceVariationCode] : 
                        ((isIrr && irrPerfUsed) ? [params.advancedOptions.irrPerformanceVariationLabel || params.advancedOptions.irrPerformanceVariationCode] : null),
                    });
                  })
                  columns.push({ id: "id", field: "id", label: "Id", dataType: "number", hiddenByDefault: true });
                  columns.push({ id: "resourceId", field: "resourceId", label: "Resource Id", dataType: "number", hiddenByDefault: true });
                  columns.push({ id: "groupNodeId", field: "groupNodeId", label: "Group Node Id", dataType: "number", hiddenByDefault: true });
                  columns.push({ id: "parentGroupNodeId", field: "parentGroupNodeId", label: "Parent Group Node Id", dataType: "number", hiddenByDefault: true });
                  self.columnSet.changeColumnOrderWithIdList(columns.map(c => c.id), false);
                  return columns;
                },
                rowData: "dataSets.performance.dataSet.lineItems",
                subRowGroupKeyGetter: function (row) { return row.id; },
                subRowFactory: function (row) {
                  if (row.benchmarks) {
                    const subRows = [];
                    row.benchmarks.forEach(b => subRows.push(b));
                    return subRows;
                  }
                },
                groups: function () {
                  const groups = [];
                  groups.push({
                    id: "all",
                    label: "All",
                    dataRowsForGroups: true,
                    field: "parentGroupNodeId",
                    groupValueField: "groupNodeId",
                    htmlFormatter: function (value, valueContext) {
                      const span = $("<span>");
                      span.text(valueContext.row._backingRow.label);
                      return span;
                    }
                  });
                  return groups;
                },
                groupBy: [{ id: "all" }],
                aggregateRow: { display: "inline", groupHeaderColspan: 1 },
              },
              dataSources: [
                { name: "performance", ref: { elementName: "Root", dataSourceName: "performance" } }
              ]
            }
          ],
          dataSources: [horizonPerformanceDataSet]
        }
      };

      return report;
    };
  }
}